import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
// import { useMatomo } from '../contexts/SimpleMatomoContext';
import MapComponent from '../components/MapComponent';
import { formatSize, formatPrice } from '../utils/formatters';
import { apiUrl, storageUrl, withStorageFallback, normalizeMediaUrl } from '../utils/apiBase';
import pwaService from '../services/pwaService';
import L from 'leaflet';

const PropertySearchPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading } = useAuth();
  // const { trackPropertySearch, trackCustomEvent } = useMatomo();
  const trackPropertySearch = () => {}; // Mock function
  const trackCustomEvent = () => {}; // Mock function
  
  const [properties, setProperties] = useState([]);
  /** All properties for map pins (loaded once with high per_page); list stays paginated */
  const [mapProperties, setMapProperties] = useState([]);
  const [favoriteProperties, setFavoriteProperties] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [regionQuery, setRegionQuery] = useState('');
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    minSize: '',
    maxSize: '',
    propertyType: '',
    propertyCategory: '',
    ownerOnly: false
  });
  const [selectionBounds, setSelectionBounds] = useState(null);
  /** In PWA, selection drawing is only enabled after user taps "Activate selection"; avoids touch conflicts. */
  const [pwaSelectionModeActive, setPwaSelectionModeActive] = useState(false);
  const [propertiesPage, setPropertiesPage] = useState(1);
  const [hasMoreProperties, setHasMoreProperties] = useState(true);
  const [loadingMoreProperties, setLoadingMoreProperties] = useState(false);
  /** Total matching filters from API (list is paginated; map may load more in one request). */
  const [listTotal, setListTotal] = useState(null);
  const loadMoreSentinelRef = useRef(null);
  // Guard against repeated subscription checks, but allow re-check when user/token changes.
  const subscriptionCheckedRef = useRef({ key: null });

  const parseCoord = useCallback((value) => {
    if (value === null || value === undefined || value === '') return null;
    // Be tolerant to "8,5380" style decimals.
    const n = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }, []);

  const getPropertyCoords = useCallback((property) => {
    if (!property) return null;

    const candidates = [
      [property.latitude, property.longitude],
      [property.gps_latitude, property.gps_longitude],
      [property.manual_latitude, property.manual_longitude],
      // Be resilient if some endpoints return different keys.
      [property.lat, property.lng],
    ];

    for (const [latRaw, lngRaw] of candidates) {
      const lat = parseCoord(latRaw);
      const lng = parseCoord(lngRaw);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue;
      return { lat, lng };
    }

    return null;
  }, [parseCoord]);

  const spreadDuplicateMarkers = useCallback((markers) => {
    // If multiple properties have identical coords, Leaflet renders them stacked.
    // Spread them slightly (a few meters) so all pins are visible/clickable.
    const groups = new Map();
    // Use a coarser grouping precision so "almost identical" pins still spread.
    // 4 decimals ~= 11m; good enough to avoid icon overlap at typical zoom levels.
    const precision = 4;
    markers.forEach((m) => {
      const key = `${Number(m.lat).toFixed(precision)},${Number(m.lng).toFixed(precision)}`;
      const arr = groups.get(key) || [];
      arr.push(m);
      groups.set(key, arr);
    });

    const out = [];
    for (const arr of groups.values()) {
      if (arr.length === 1) {
        out.push(arr[0]);
        continue;
      }

      const baseLat = arr[0].lat;
      const baseLng = arr[0].lng;
      const n = arr.length;
      // ~20–45m radius depending on count (keeps the pins clearly separated)
      const baseRadius = 0.00028; // ~31m in lat degrees
      for (let i = 0; i < n; i++) {
        const angle = (2 * Math.PI * i) / n;
        const ring = Math.floor(i / 8); // add rings for very dense duplicates
        const radius = baseRadius * (1 + ring * 0.9);
        const dLat = radius * Math.cos(angle);
        // Adjust lng delta by latitude so the circle is more uniform visually
        const latRad = (baseLat * Math.PI) / 180;
        const dLng = (radius * Math.sin(angle)) / Math.max(0.25, Math.cos(latRad));

        out.push({
          ...arr[i],
          lat: baseLat + dLat,
          lng: baseLng + dLng,
          _originalLat: baseLat,
          _originalLng: baseLng,
          _stackSize: n,
        });
      }
    }

    return out;
  }, []);

  // Region coordinates mapping
  const regionCoordinates = {
    'London': [51.505, -0.09],
    'Panamá': [8.5380, -80.7821],
    'Panama': [8.5380, -80.7821],
    'test': [51.505, -0.09], // Default to London for test
    // Add more regions as needed
  };

  const checkSubscription = useCallback(async () => {
    const token = localStorage.getItem('token');
    const userId = user?.id ?? 'anon';
    const key = `${userId}:${token || ''}`;

    if (subscriptionCheckedRef.current.key === key) return;
    subscriptionCheckedRef.current.key = key;
    try {
      console.log('PropertySearchPage - Checking subscription with token:', token ? 'exists' : 'missing');
      const response = await fetch(apiUrl('/subscriptions/active'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      
      console.log('PropertySearchPage - Subscription check response status:', response.status);
      
      if (!response.ok) {
        // No active subscription, redirect to preview page
        console.log('PropertySearchPage - Response not ok, redirecting to /preview');
        navigate('/preview');
        return;
      }
      
      const data = await response.json();
      console.log('PropertySearchPage - Subscription check data:', data);
      
      if (!data.success || !data.data) {
        // No active subscription, redirect to preview page
        console.log('PropertySearchPage - No subscription data, redirecting to /preview');
        navigate('/preview');
        return;
      }
      
      // User has active subscription, allow access to search page
      console.log('PropertySearchPage - User has active subscription, allowing access to search page');
    } catch (error) {
      console.error('PropertySearchPage - Error checking subscription:', error);
      // On error, redirect to preview page
      navigate('/preview');
    }
  }, [navigate, user?.id]);

  const fetchRegions = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('/properties/regions'));
      const data = await response.json();
      
      if (data.success) {
        setRegions(data.data);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  }, []);

  const fetchFavoriteProperties = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoadingFavorites(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl('/favorites'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error fetching favorites:', response.status, errorText);
        // If unauthorized, don't show error - just return empty array
        if (response.status === 401) {
          setFavoriteProperties([]);
          return;
        }
        // For other errors, try to parse JSON
        try {
          const errorData = JSON.parse(errorText);
          console.error('Error data:', errorData);
        } catch (e) {
          console.error('Could not parse error response');
        }
        setFavoriteProperties([]);
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        setFavoriteProperties(data.data || []);
      } else {
        console.error('Favorites API returned success=false:', data);
        setFavoriteProperties([]);
      }
    } catch (error) {
      console.error('Error fetching favorite properties:', error);
      setFavoriteProperties([]);
    } finally {
      setLoadingFavorites(false);
    }
  }, [isAuthenticated]);

  const filteredRegions = regions.filter((region) =>
    regionQuery ? region.toLowerCase().includes(regionQuery.toLowerCase()) : true
  );

  const fetchPropertiesByRegion = useCallback(async (page = 1, append = false) => {
    if (append) setLoadingMoreProperties(true);
    try {
      // Initial load: one page for fast first paint; button + infinite scroll load more
      const perPage = append ? 50 : 20;

      if (!selectedRegion) {
        const params = new URLSearchParams();
        if (filters.minPrice) params.append('min_price', filters.minPrice);
        if (filters.maxPrice) params.append('max_price', filters.maxPrice);
        if (filters.minSize) params.append('min_size', filters.minSize);
        if (filters.maxSize) params.append('max_size', filters.maxSize);
        if (filters.propertyType) params.append('property_type', filters.propertyType);
        if (filters.propertyCategory) params.append('property_category', filters.propertyCategory);
        if (filters.ownerOnly) params.append('owner_only', '1');
        params.set('page', String(page));
        params.set('per_page', String(perPage));

        const response = await fetch(`${apiUrl('/properties')}?${params.toString()}`, {
          headers: { 'Accept': 'application/json' },
        });
        const data = await response.json();

        if (data.success) {
          const items = data.data || [];
          if (append) {
            setProperties((prev) => [...prev, ...items]);
          } else {
            setProperties(items);
            setListTotal(typeof data.total === 'number' ? data.total : null);
          }
          const lastPage = data.last_page != null ? data.last_page : 1;
          const currentPage = data.current_page != null ? data.current_page : page;
          setHasMoreProperties(currentPage < lastPage);
          setPropertiesPage(currentPage);
          if (!append) trackPropertySearch('all', data.total != null ? data.total : items.length, filters);
        } else {
          if (!append) {
            setProperties([]);
            setListTotal(null);
          }
          setHasMoreProperties(false);
        }
        return;
      }

      const response = await fetch(apiUrl('/properties/by-region'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          region: selectedRegion,
          min_price: filters.minPrice || null,
          max_price: filters.maxPrice || null,
          min_size: filters.minSize || null,
          max_size: filters.maxSize || null,
          property_type: filters.propertyType || null,
          property_category: filters.propertyCategory || null,
          owner_only: filters.ownerOnly ? 1 : 0,
          page,
          per_page: perPage,
        }),
      });
      const data = await response.json();

      if (data.success) {
        const items = data.data || [];
        if (append) {
          setProperties((prev) => [...prev, ...items]);
        } else {
          setProperties(items);
          setListTotal(typeof data.total === 'number' ? data.total : null);
        }
        const lastPage = data.last_page != null ? data.last_page : 1;
        const currentPage = data.current_page != null ? data.current_page : page;
        setHasMoreProperties(currentPage < lastPage);
        setPropertiesPage(currentPage);
        if (!append) trackPropertySearch(selectedRegion, data.total != null ? data.total : items.length, filters);
      } else {
        if (!append) {
          setProperties([]);
          setListTotal(null);
        }
        setHasMoreProperties(false);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      if (!append) {
        setProperties([]);
        setListTotal(null);
      }
      setHasMoreProperties(false);
    } finally {
      if (append) setLoadingMoreProperties(false);
    }
  }, [selectedRegion, filters]);

  /** Load all properties for map pins (one request, high per_page); does not affect list pagination */
  const fetchPropertiesForMap = useCallback(async () => {
    const perPage = 2000;
    try {
      if (!selectedRegion) {
        const params = new URLSearchParams();
        if (filters.minPrice) params.append('min_price', filters.minPrice);
        if (filters.maxPrice) params.append('max_price', filters.maxPrice);
        if (filters.minSize) params.append('min_size', filters.minSize);
        if (filters.maxSize) params.append('max_size', filters.maxSize);
        if (filters.propertyType) params.append('property_type', filters.propertyType);
        if (filters.propertyCategory) params.append('property_category', filters.propertyCategory);
        if (filters.ownerOnly) params.append('owner_only', '1');
        params.set('page', '1');
        params.set('per_page', String(perPage));
        const response = await fetch(`${apiUrl('/properties')}?${params.toString()}`, {
          headers: { 'Accept': 'application/json' },
        });
        const data = await response.json();
        if (data.success) setMapProperties(data.data || []);
        else setMapProperties([]);
        return;
      }
      const response = await fetch(apiUrl('/properties/by-region'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          region: selectedRegion,
          min_price: filters.minPrice || null,
          max_price: filters.maxPrice || null,
          min_size: filters.minSize || null,
          max_size: filters.maxSize || null,
          property_type: filters.propertyType || null,
          property_category: filters.propertyCategory || null,
          owner_only: filters.ownerOnly ? 1 : 0,
          page: 1,
          per_page: perPage,
        }),
      });
      const data = await response.json();
      if (data.success) setMapProperties(data.data || []);
      else setMapProperties([]);
    } catch (error) {
      console.error('Error fetching properties for map:', error);
      setMapProperties([]);
    }
  }, [selectedRegion, filters]);

  useEffect(() => {
    // Wait for auth to finish loading before checking anything
    if (isLoading) {
      return;
    }

    if (!isAuthenticated || user?.role !== 'land_seeker') {
      navigate('/login', { state: { role: 'land_seeker' } });
      return;
    }

    // Only check subscription and fetch regions when user is fully authenticated
    if (user && user.role === 'land_seeker') {
      checkSubscription();
      fetchRegions();
      fetchFavoriteProperties();
    }
    
    // Set selected region if passed from land search flow
    if (location.state?.selectedRegion) {
      setSelectedRegion(location.state.selectedRegion);
    }
  }, [isAuthenticated, user, navigate, location.state?.selectedRegion, isLoading, checkSubscription, fetchRegions, fetchFavoriteProperties]);

  useEffect(() => {
    setPropertiesPage(1);
    setHasMoreProperties(true);
    fetchPropertiesByRegion(1, false);
    fetchPropertiesForMap();
  }, [selectedRegion, filters, fetchPropertiesByRegion, fetchPropertiesForMap]);

  const loadMoreProperties = useCallback(() => {
    if (!hasMoreProperties || loadingMoreProperties) return;
    fetchPropertiesByRegion(propertiesPage + 1, true);
  }, [hasMoreProperties, loadingMoreProperties, propertiesPage, fetchPropertiesByRegion]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMoreProperties || loadingMoreProperties) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreProperties();
      },
      { rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreProperties, loadingMoreProperties, loadMoreProperties]);

  useEffect(() => {
    // Clear any active selection when switching region (but don't clear immediately after drawing).
    setSelectionBounds(null);
    setPwaSelectionModeActive(false);
  }, [selectedRegion]);

  // Refresh favorites when location changes (user navigates back to search page)
  useEffect(() => {
    if (location.pathname === '/search' && isAuthenticated && user?.role === 'land_seeker') {
      fetchFavoriteProperties();
    }
  }, [location.pathname, isAuthenticated, user, fetchFavoriteProperties]);

  /** When a selection is drawn, show properties inside bounds from mapProperties (same as map pins). Otherwise show paginated list. */
  const displayedProperties = useMemo(() => {
    if (!selectionBounds) return properties;
    const bounds = L.latLngBounds(selectionBounds);
    return mapProperties.filter((property) => {
      const coords = getPropertyCoords(property);
      if (!coords) return false;
      return bounds.contains([coords.lat, coords.lng]);
    });
  }, [selectionBounds, properties, mapProperties, getPropertyCoords]);

  /** Map shows only what matches current filter (and selection if drawn); must be before any return (hooks rule) */
  const propertiesForMap = useMemo(() => {
    if (!selectionBounds) return mapProperties;
    const bounds = L.latLngBounds(selectionBounds);
    return mapProperties.filter((property) => {
      const coords = getPropertyCoords(property);
      if (!coords) return false;
      return bounds.contains([coords.lat, coords.lng]);
    });
  }, [selectionBounds, mapProperties, getPropertyCoords]);

  // Show loading while auth is being checked
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-geist">Loading...</p>
        </div>
      </div>
    );
  }

  const handlePropertyClick = (property) => {
    navigate(`/property/${property.id}`);
  };

  const getPropertyListThumbSrc = (property) => {
    const img = property?.images?.[0];
    if (!img) return null;
    if (img.image_url) {
      // Sometimes backend returns absolute URL, sometimes relative.
      return normalizeMediaUrl(img.image_url);
    }
    if (img.file_path) return storageUrl(img.file_path);
    return null;
  };

  if (!isAuthenticated || user?.role !== 'land_seeker') {
    return null;
  }

  const mapMarkers = propertiesForMap
    .map((property) => {
      const coords = getPropertyCoords(property);
      if (!coords) return null;
      return {
        id: property.id,
        lat: coords.lat,
        lng: coords.lng,
        title: property.title,
        description: property.description,
        price: property.price,
        size: property.size,
        phone: property.phone,
        whatsapp: property.whatsapp,
        email: property.email,
        color: '#3B82F6',
      };
    })
    .filter(Boolean);

  const markerStats = (() => {
    const valid = mapMarkers.length;
    const invalid = Math.max(0, propertiesForMap.length - valid);
    const precision = 4;
    const uniquePositions = new Set(
      mapMarkers.map((m) => `${Number(m.lat).toFixed(precision)},${Number(m.lng).toFixed(precision)}`)
    ).size;
    const overlapped = Math.max(0, valid - uniquePositions);
    return { valid, invalid, uniquePositions, overlapped };
  })();

  const mapMarkersSpread = spreadDuplicateMarkers(mapMarkers);

  // Calculate map bounds based on properties
  const getMapBounds = () => {
    if (mapMarkersSpread.length === 0) {
      // No properties, use region center
      return {
        center: regionCoordinates[selectedRegion] || [51.505, -0.09],
        zoom: 13,
        bounds: null
      };
    }

    if (mapMarkersSpread.length === 1) {
      // Single property, center on it with some padding
      const marker = mapMarkersSpread[0];
      const padding = 0.01; // Small padding around single marker
      const lat = parseFloat(marker.lat);
      const lng = parseFloat(marker.lng);
      
      return {
        center: [lat, lng],
        zoom: 15,
        bounds: [
          [lat - padding, lng - padding],
          [lat + padding, lng + padding]
        ]
      };
    }

    // Multiple properties, calculate bounds
    const lats = mapMarkersSpread.map(marker => parseFloat(marker.lat));
    const lngs = mapMarkersSpread.map(marker => parseFloat(marker.lng));
    
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    
    // Add some padding to the bounds
    const latPadding = Math.max(0.01, (maxLat - minLat) * 0.1);
    const lngPadding = Math.max(0.01, (maxLng - minLng) * 0.1);
    
    return {
      center: [centerLat, centerLng],
      zoom: 13, // Will be overridden by fitBounds
      bounds: [
        [minLat - latPadding, minLng - lngPadding],
        [maxLat + latPadding, maxLng + lngPadding]
      ]
    };
  };

  const mapBounds = getMapBounds();

  // Debug logging
  console.log('PropertySearchPage - Selected region:', selectedRegion);
  console.log('PropertySearchPage - Properties count:', properties.length);
  console.log('PropertySearchPage - Map markers count:', mapMarkers.length);
  console.log('PropertySearchPage - Map marker unique positions:', markerStats.uniquePositions);
  console.log('PropertySearchPage - Map bounds:', mapBounds);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  ← {t('searchPage.backToMainMenu')}
                </button>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 text-center sm:text-left flex-1">
                🔍 {t('searchPage.title')}
              </h1>
            </div>
            <p className="text-gray-600">{t('searchPage.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Filters Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">🔍 {t('searchPage.filters')}</h2>
                
                {/* Region Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('searchPage.region')}
                  </label>
                  <input
                    type="text"
                    value={regionQuery}
                    onChange={(e) => setRegionQuery(e.target.value)}
                    placeholder={t('searchPage.searchRegionsPlaceholder')}
                    className="mb-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={selectedRegion}
                    onChange={(e) => {
                      setSelectedRegion(e.target.value);
                      // Track region selection
                      trackCustomEvent('Search', 'Region Selected', e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('searchPage.allRegions')}</option>
                    {filteredRegions.map((region, index) => (
                      <option key={`${region}-${index}`} value={region}>{region}</option>
                    ))}
                  </select>
                  {selectedRegion && (
                    <button
                      type="button"
                      onClick={() => setSelectedRegion('')}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      {t('searchPage.clearSelection')}
                    </button>
                  )}
                </div>

                {/* Price Range */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('searchPage.priceRange')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder={t('searchPage.min')}
                      value={filters.minPrice}
                      onChange={(e) => setFilters({...filters, minPrice: e.target.value})}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      placeholder={t('searchPage.max')}
                      value={filters.maxPrice}
                      onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Size Range */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('searchPage.sizeRange')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder={t('searchPage.min')}
                      value={filters.minSize}
                      onChange={(e) => setFilters({...filters, minSize: e.target.value})}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      placeholder={t('searchPage.max')}
                      value={filters.maxSize}
                      onChange={(e) => setFilters({...filters, maxSize: e.target.value})}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Property Type */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('searchPage.propertyType')}
                  </label>
                  <select
                    value={filters.propertyType}
                    onChange={(e) => setFilters({...filters, propertyType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('searchPage.allTypes')}</option>
                    <option value="sale">🏠 {t('searchPage.forSale')}</option>
                    <option value="rent">🏠 {t('searchPage.forRent')}</option>
                  </select>
                </div>

                {/* Property Category */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('searchPage.propertyCategory')}
                  </label>
                  <select
                    value={filters.propertyCategory}
                    onChange={(e) => setFilters({...filters, propertyCategory: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('searchPage.allCategories')}</option>
                    <option value="land_lot">{t('propertyDetailPage.categories.land_lot')}</option>
                    <option value="house">{t('propertyDetailPage.categories.house')}</option>
                    <option value="apartment">{t('propertyDetailPage.categories.apartment')}</option>
                    <option value="commercial">{t('propertyDetailPage.categories.commercial')}</option>
                    <option value="other">{t('propertyDetailPage.categories.other')}</option>
                  </select>
                </div>

                {/* Owner-only sales */}
                <div className="mb-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={filters.ownerOnly}
                      onChange={(e) => setFilters({ ...filters, ownerOnly: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    {t('searchPage.onlyOwnerSales')}
                  </label>
                </div>

                {/* Results Count */}
                <div className="text-sm text-gray-600">
                  {t('searchPage.resultsCount', { count: properties.length })}
                  {selectionBounds ? ` • ${t('searchPage.insideSelectionCount', { count: displayedProperties.length })}` : ''}
                </div>
              </div>

              {/* Favorite Properties Container */}
              <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h2 className="text-xl font-semibold">❤️ {t('searchPage.favoritesTitle')}</h2>
                  {!loadingFavorites && favoriteProperties.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const withCoords = favoriteProperties.filter((p) => {
                          const lat = Number(p.latitude ?? p.gps_latitude ?? p.manual_latitude);
                          const lng = Number(p.longitude ?? p.gps_longitude ?? p.manual_longitude);
                          return Number.isFinite(lat) && Number.isFinite(lng);
                        });
                        if (withCoords.length === 0) {
                          toast.error(t('searchPage.startPropertyTourNoLocations'));
                          return;
                        }
                        const points = withCoords.map((p) => {
                          const lat = Number(p.latitude ?? p.gps_latitude ?? p.manual_latitude);
                          const lng = Number(p.longitude ?? p.gps_longitude ?? p.manual_longitude);
                          return `${lat},${lng}`;
                        });
                        let url;
                        if (points.length === 1) {
                          url = `https://www.google.com/maps/?q=${encodeURIComponent(points[0])}`;
                        } else if (points.length === 2) {
                          url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(points[0])}&destination=${encodeURIComponent(points[1])}&travelmode=driving`;
                        } else {
                          const origin = points[0];
                          const destination = points[points.length - 1];
                          const waypoints = points.slice(1, -1).join('|');
                          url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&waypoints=${encodeURIComponent(waypoints)}&travelmode=driving`;
                        }
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                      className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      🗺️ {t('searchPage.startPropertyTour')}
                    </button>
                  )}
                </div>

                {loadingFavorites ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">{t('searchPage.loadingFavorites')}</p>
                  </div>
                ) : favoriteProperties.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500 text-sm">{t('searchPage.noFavorites')}</p>
                    <p className="text-gray-400 text-xs mt-1">{t('searchPage.noFavoritesHint')}</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {favoriteProperties.map((property) => (
                      <div
                        key={property.id}
                        className="border rounded-lg p-3 cursor-pointer transition-colors border-gray-200 hover:border-blue-400 hover:shadow-md"
                        onClick={() => handlePropertyClick(property)}
                      >
                        <div className="flex items-start gap-3">
                          {property.images && property.images.length > 0 && (
                            <img
                              src={normalizeMediaUrl(property.images[0].image_url) || storageUrl(property.images[0].file_path)}
                              alt={property.title}
                              className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                              onError={(e) => {
                                if (!e.target.dataset.fallbackTried) {
                                  e.target.dataset.fallbackTried = '1';
                                  e.target.src = withStorageFallback(e.target.src);
                                  return;
                                }
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm truncate">{property.title}</h3>
                            <p className="text-gray-600 text-xs truncate mb-1">{property.address || property.region}</p>
                            {property.user?.is_real_estate_business && (
                              <p className="text-xs text-amber-700 mb-1">{t('searchPage.agentReportedNotice')}</p>
                            )}
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-green-600 font-semibold">
                                {formatPrice(property.price, property.currency)}
                              </span>
                              {property.size && (
                                <span className="text-gray-500">
                                  • {formatSize(property.size, property.size_unit)}
                                </span>
                              )}
                            </div>
                            {property.favorite_note && (
                              <p className="text-xs text-gray-500 mt-1 truncate" title={property.favorite_note}>
                                📝 {property.favorite_note}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Map and Properties */}
            <div className="lg:col-span-2">
              {/* Map */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                  <h2 className="text-xl font-semibold">🗺️ {t('searchPage.mapView')}</h2>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    {pwaService.isStandalone() && !pwaSelectionModeActive && (
                      <button
                        type="button"
                        onClick={() => setPwaSelectionModeActive(true)}
                        className="px-4 py-2 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                      >
                        {t('searchPage.activateSelectionMode')}
                      </button>
                    )}
                    {selectionBounds ? (
                      <>
                        <span className="text-blue-600 font-medium">
                          {t('searchPage.propertiesInsideSelection', { count: displayedProperties.length })}
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectionBounds(null)}
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {t('searchPage.clearSelection')}
                        </button>
                      </>
                    ) : pwaSelectionModeActive || !pwaService.isStandalone() ? (
                      <span className="text-gray-500">
                        {t('searchPage.drawSelectionHint')}
                      </span>
                    ) : null}
                  </div>
                </div>
                {(markerStats.invalid > 0 || markerStats.overlapped > 0) && (
                  <div className="text-xs text-gray-500 mb-3">
                    {t('searchPage.mapPins', { valid: markerStats.valid, total: propertiesForMap.length })}
                    {markerStats.overlapped > 0 ? ` • ${t('searchPage.overlappingPins', { count: markerStats.overlapped })}` : ''}
                    {markerStats.invalid > 0 ? ` • ${t('searchPage.missingCoordinates', { count: markerStats.invalid })}` : ''}
                  </div>
                )}

                <MapComponent
                  height="400px"
                  center={mapBounds.center}
                  zoom={mapBounds.zoom}
                  bounds={mapBounds.bounds}
                  markers={mapMarkersSpread}
                  onMarkerClick={handlePropertyClick}
                  showControls={true}
                  enableSelection={pwaService.isStandalone() ? pwaSelectionModeActive : true}
                  selectionBounds={selectionBounds}
                  onSelectionChange={setSelectionBounds}
                  selectionOptions={{ color: '#2563eb', weight: 1.5, dashArray: '6', fillOpacity: 0.1 }}
                />
              </div>

              {/* Properties List */}
              <div className="bg-gray-50 rounded-lg shadow-sm p-4 md:p-6">
                <h2 className="text-xl font-semibold mb-2">
                  🏠 {selectedRegion ? t('searchPage.propertiesInRegion', { region: selectedRegion }) : t('searchPage.allProperties')}
                  {' '}
                  (
                  {!selectionBounds && listTotal != null
                    ? t('searchPage.listLoadedOfTotal', { loaded: properties.length, total: listTotal })
                    : displayedProperties.length}
                  )
                </h2>
                {!selectionBounds && listTotal != null && listTotal > properties.length && (
                  <p className="text-sm text-gray-600 mb-4">
                    {t('searchPage.listPaginationExplainer')}
                  </p>
                )}

                {selectionBounds && (
                  <p className="text-sm text-blue-600 mb-4">
                    {t('searchPage.showingInsideSelection', { count: properties.length })}
                  </p>
                )}
                
                {displayedProperties.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      {selectionBounds
                        ? t('searchPage.noPropertiesInSelection')
                        : selectedRegion ? t('searchPage.noPropertiesInRegion', { region: selectedRegion }) : t('searchPage.noProperties')}
                    </p>
                    {selectionBounds && (
                      <button
                        type="button"
                        onClick={() => setSelectionBounds(null)}
                        className="mt-4 text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        {t('searchPage.clearSelection')}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {displayedProperties.map((property) => (
                      <div
                        key={property.id}
                        className="border border-gray-200 rounded-lg overflow-hidden bg-white cursor-pointer transition-colors hover:border-gray-300 md:flex md:items-stretch"
                        onClick={() => handlePropertyClick(property)}
                      >
                        {property.images && property.images.length > 0 && (
                          <div className="w-full md:w-32 md:order-2 md:flex-shrink-0">
                            <img
                              src={getPropertyListThumbSrc(property)}
                              alt={property.title}
                              className="w-full aspect-video object-cover md:aspect-auto md:h-full"
                              onError={(e) => {
                                if (!e.target.dataset.fallbackTried) {
                                  e.target.dataset.fallbackTried = '1';
                                  e.target.src = withStorageFallback(e.target.src);
                                  return;
                                }
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        <div className="p-3 md:p-4 md:flex-1 md:order-1">
                          <h3 className="font-semibold text-lg">{property.title}</h3>
                          <p className="text-gray-600 text-sm mb-2">{property.description}</p>
                          {property.user?.is_real_estate_business && (
                            <p className="text-sm text-amber-700 mb-2">{t('searchPage.agentReportedNotice')}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 text-sm">
                            <span className="text-green-600 font-semibold">
                              {formatPrice(property.price, property.currency)}
                            </span>
                            <span className="text-gray-500">
                              {formatSize(property.size, property.size_unit)}
                            </span>
                            <span className="text-gray-500">{property.region}</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              property.property_type === 'sale'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              🏠 {property.property_type === 'sale' ? t('searchPage.sale') : t('searchPage.rent')}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Sentinel for infinite scroll: load more when this enters viewport */}
                    {displayedProperties.length > 0 && (
                      <div ref={loadMoreSentinelRef} className="h-4 flex flex-col items-center justify-center gap-3 py-4">
                        {loadingMoreProperties && (
                          <span className="text-sm text-gray-500">{t('searchPage.loadingMore')}</span>
                        )}
                        {!selectionBounds && hasMoreProperties && (
                          <button
                            type="button"
                            onClick={loadMoreProperties}
                            disabled={loadingMoreProperties}
                            className="px-5 py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                          >
                            {t('searchPage.loadMore')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PropertySearchPage;